import reduce from 'lodash/reduce';

import { getIsoDateString, isDate, parseDate } from '@/utils/dates/dates';
import { isValid, parseISO } from 'date-fns';

/**
 * Takes an object and returns a deep copy of it
 * with all dates transformed to an ISO String.
 *
 * @param obj Object
 * @returns Object
 */
export const transformDates = <R = Record<string, unknown>, T = unknown>(
  obj: T,
): R =>
  reduce(
    obj as Record<string, unknown>,
    (acc, value, key) => {
      if (isDate(value)) {
        acc[key] = getIsoDateString(value);
      } else if (typeof value === 'object' && value !== null) {
        if (value instanceof Array) {
          acc[key] = value.map((ele) => {
            if (typeof ele === 'object') {
              return transformDates(ele as Record<string, unknown>);
            }

            return ele as unknown;
          });
        } else {
          acc[key] = transformDates(value as Record<string, unknown>);
        }
      } else {
        acc[key] = value;
      }
      return acc;
    },
    {} as Record<string, unknown>,
  ) as R;

export const transformDateStrings = <R = Record<string, unknown>, T = unknown>(
  obj: T,
): R => {
  const IsoDateFormatRegex = /\d{4}-\d{2}-\d{2}/;
  return reduce(
    obj as Record<string, unknown>,
    (acc, value, key) => {
      if (typeof value === 'string' && IsoDateFormatRegex.test(value)) {
        const parsedAsDate = Date.parse(value);
        acc[key] = Number.isNaN(parsedAsDate) ? value : parseDate(value);
        if (!isValid(acc[key])) {
          const parsedISODate = parseISO(value);
          acc[key] = isValid(parsedISODate) ? parsedISODate : value;
        }
      } else if (typeof value === 'object' && value !== null) {
        if (value instanceof Array) {
          // we just assume that it's an object for now
          // @todo extend to handle native values and DateStrings
          acc[key] = value.map((ele) => {
            if (typeof ele === 'object') {
              return transformDateStrings(ele as Record<string, unknown>);
            }

            return ele as unknown;
          });
        } else if (isDate(value)) {
          acc[key] = value;
        } else {
          acc[key] = transformDateStrings(value as Record<string, unknown>);
        }
      } else {
        acc[key] = value;
      }

      return acc;
    },
    {} as Record<string, unknown>,
  ) as R;
};
